import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    formation: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    toast({
      title: "Message envoyé !",
      description: "Nous vous recontacterons dans les plus brefs délais.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      formation: '',
      message: ''
    });
  };

  return (
    <div>
      {/* Hero Section */}
      <div 
        className="relative h-[350px] flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&h=350&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/60 to-black/70"></div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="mb-4 text-sm">
            <span className="text-gray-300">Accueil</span>
            <span className="mx-2 text-red-500">/</span>
            <span className="text-red-500">Contact</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wide">
            Contactez-nous
          </h1>
        </div>
      </div>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Parlons de votre projet
                </h2>
                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                  Vous avez un besoin en formation ? Vous souhaitez obtenir un devis personnalisé ? 
                  N'hésitez pas à nous contacter, nous vous répondrons dans les plus brefs délais.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-red-600 text-white p-3 rounded-lg">
                      <Phone size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Téléphone</h3>
                      <a 
                        href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                        className="text-gray-600 hover:text-red-600 transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-red-600 text-white p-3 rounded-lg">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                      <a 
                        href={`mailto:${companyInfo.email}`}
                        className="text-gray-600 hover:text-red-600 transition-colors break-all"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-red-600 text-white p-3 rounded-lg">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Zone d'intervention</h3>
                      <p className="text-gray-600">
                        Nous intervenons sur toute la France
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 p-6 bg-red-50 rounded-lg border-l-4 border-red-600">
                  <h3 className="font-bold text-gray-900 mb-2">Délai de réponse</h3>
                  <p className="text-gray-700">
                    Nous nous engageons à vous répondre sous 24h ouvrées maximum.
                  </p>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-lg shadow-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Demande de devis
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent transition-colors"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent transition-colors"
                        placeholder="votre@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent transition-colors"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Entreprise / Organisme
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent transition-colors"
                      placeholder="Nom de votre organisation"
                    />
                  </div>

                  <div>
                    <label htmlFor="formation" className="block text-sm font-semibold text-gray-700 mb-2">
                      Formation souhaitée
                    </label>
                    <select
                      id="formation"
                      name="formation"
                      value={formData.formation}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent transition-colors"
                    >
                      <option value="">Sélectionnez une catégorie</option>
                      <option value="incendie">Formations Incendie</option>
                      <option value="secourisme">Formations Secourisme</option>
                      <option value="prevention">Formations Prévention sécurité</option>
                      <option value="electrique">Habilitation électrique</option>
                      <option value="securite-privee">Sécurité privée</option>
                      <option value="attentat">Risque attentat</option>
                      <option value="autre">Autre / Formation personnalisée</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Votre message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent transition-colors resize-none"
                      placeholder="Décrivez votre besoin en formation..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-red-600 text-white px-6 py-4 rounded-md hover:bg-red-700 transition-colors font-bold text-lg flex items-center justify-center gap-2"
                  >
                    <Send size={20} />
                    ENVOYER MA DEMANDE
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;